/* Dosey redesign: small behaviours for the dosey-* sections. */

/**
 * A horizontally scrolling list with previous/next buttons.
 * Markup: <dosey-rail> ... [data-rail-track] ... [data-rail-prev] [data-rail-next]
 */
class DoseyRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.scrollByCard(-1));
    this.next?.addEventListener('click', () => this.scrollByCard(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.update();
  }

  scrollByCard(direction) {
    const card = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
  }
}

/**
 * A video with a single play/pause button; the native controls stay hidden
 * until the viewer starts it.
 */
class DoseyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-video-toggle]');
    if (!this.video || !this.button) return;

    this.video.removeAttribute('controls');
    this.button.addEventListener('click', () => this.toggle());
    this.video.addEventListener('play', () => this.setPlaying(true));
    this.video.addEventListener('pause', () => this.setPlaying(false));
  }

  toggle() {
    if (this.video.paused) {
      document.querySelectorAll('dosey-video video').forEach((other) => {
        if (other !== this.video) other.pause();
      });
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  setPlaying(playing) {
    this.classList.toggle('is-playing', playing);
    this.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }
}

if (!customElements.get('dosey-rail')) customElements.define('dosey-rail', DoseyRail);
if (!customElements.get('dosey-video')) customElements.define('dosey-video', DoseyVideo);

/**
 * Funnel buy box: colour swatches update the variant, image and price, and the
 * thumbnail row swaps the main image. The add to bag form itself is the
 * theme's product-form, so the cart drawer opens as it does on product pages.
 */
class DoseyBuyBox extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('[data-buy-box-image]') || this.querySelector('.fn-offer__main img');
    this.input = this.querySelector('[data-buy-box-input]');
    this.optionLabel = this.querySelector('[data-buy-box-option]');
    this.price = this.querySelector('[data-buy-box-price]');
    this.buttonPrice = this.querySelector('[data-buy-box-button-price]');

    this.querySelectorAll('.fn-swatch input').forEach((radio) => {
      radio.addEventListener('change', () => this.selectVariant(radio));
    });
    this.querySelectorAll('[data-thumb-src]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.showImage(thumb.dataset.thumbSrc);
        this.querySelectorAll('[data-thumb-src]').forEach((t) => t.classList.toggle('is-current', t === thumb));
      });
    });
  }

  selectVariant(radio) {
    if (this.input) this.input.value = radio.value;
    if (this.optionLabel) this.optionLabel.textContent = radio.dataset.option;
    if (this.price) this.price.textContent = radio.dataset.price;
    if (this.buttonPrice) this.buttonPrice.textContent = radio.dataset.price;
    if (radio.dataset.image) this.showImage(radio.dataset.image);
  }

  showImage(src) {
    if (!this.image || !src) return;
    this.image.removeAttribute('srcset');
    this.image.src = src;
  }
}

if (!customElements.get('dosey-buy-box')) customElements.define('dosey-buy-box', DoseyBuyBox);

/**
 * Announcement bar (scrolling promotion with the custom class
 * "dsy-announcement-static"): shows one message at a time and fades to the next,
 * instead of a moving ticker. Respects reduced motion by holding the first message.
 */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.dsy-announcement-static').forEach((bar) => {
    const items = [...bar.querySelectorAll('.promotion:first-of-type .promotion__item')];
    if (items.length < 2) return;
    items.forEach((item) => item.classList.add('dsy-rotating'));
    let index = 0;
    const show = (i) => items.forEach((item, n) => {
      item.classList.toggle('is-active', n === i);
      item.querySelectorAll('a').forEach((a) => (a.tabIndex = n === i ? 0 : -1));
    });
    show(0);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let timer = setInterval(() => { index = (index + 1) % items.length; show(index); }, 4500);
    bar.addEventListener('mouseenter', () => clearInterval(timer));
    bar.addEventListener('mouseleave', () => { timer = setInterval(() => { index = (index + 1) % items.length; show(index); }, 4500); });
  });
});

/**
 * Product page videos (gallery and media): no native controls; a tap plays or
 * pauses. Videos added later (gallery slides load lazily) are handled too.
 */
(() => {
  const tidy = (video) => {
    if (video.dataset.dsyVideo) return;
    video.dataset.dsyVideo = '1';
    video.controls = false;
    video.removeAttribute('controls');
    video.setAttribute('playsinline', '');
    video.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
  };
  const scan = () => document.querySelectorAll('.product video, media-gallery video, .product__media video').forEach(tidy);
  document.addEventListener('DOMContentLoaded', () => {
    scan();
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; scan(); });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();

/**
 * Hover video on product images (Find your Dosey). With a mouse the video
 * fades in and plays while the pointer is over the card; on touch screens it
 * plays while the card is mostly in view. The video only loads on first use.
 */
class DoseyHoverVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.muted = true;
    this.video.removeAttribute('controls');

    // Optional start point: the video begins there and loops back to it (not to 0).
    this.start = parseFloat(this.dataset.start) || 0;
    if (this.start > 0) {
      this.video.loop = false;
      const seekToStart = () => {
        if (this.video.duration && this.start < this.video.duration) this.video.currentTime = this.start;
      };
      this.video.addEventListener('loadedmetadata', seekToStart, { once: true });
      if (this.video.readyState >= 1) seekToStart();
      this.video.addEventListener('ended', () => {
        seekToStart();
        if (this.wanted) this.video.play().catch(() => {});
      });
    }
    const card = this.closest('.dsy-featured__item') || this.parentElement;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Loading indicator while the video buffers after a hover / scroll into view.
    this.spinner = document.createElement('span');
    this.spinner.className = 'dsy-hover-video__loading';
    this.spinner.setAttribute('aria-hidden', 'true');
    this.appendChild(this.spinner);
    this.video.addEventListener('playing', () => {
      this.classList.remove('is-loading');
      if (this.wanted) this.classList.add('is-playing');
    });
    this.video.addEventListener('waiting', () => this.wanted && this.classList.add('is-loading'));

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // Desktop: start buffering as the section nears the screen, so hovers are instant.
      this.preloader = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.video.preload = 'auto';
            this.video.load();
            this.preloader.disconnect();
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.preloader.observe(card);
      card.addEventListener('pointerenter', () => this.play());
      card.addEventListener('pointerleave', () => this.stop());
    } else {
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => (entry.intersectionRatio >= 0.6 ? this.play() : this.stop())),
        { threshold: [0, 0.6, 1] }
      );
      this.observer.observe(card);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.preloader?.disconnect();
  }

  play() {
    this.wanted = true;
    this.video.preload = 'auto';
    if (this.video.readyState < 3) this.classList.add('is-loading');
    const playing = this.video.play();
    if (playing) playing.catch(() => this.classList.remove('is-loading'));
  }

  stop() {
    this.wanted = false;
    this.classList.remove('is-playing', 'is-loading');
    this.video.pause();
  }
}
if (!customElements.get('dosey-hover-video')) customElements.define('dosey-hover-video', DoseyHoverVideo);

/**
 * Mobile sticky "Add to bag" on product pages. It shows whenever the page's
 * own Add to bag button is off screen, mirrors its label and price, and adds
 * the variant chosen above by pressing that same button.
 */
(() => {
  const init = () => {
    const source = document.querySelector('.product__info-wrapper .product__block--buy_buttons button[name="add"]');
    if (!source || document.querySelector('.dsy-sticky-atc')) return;

    const bar = document.createElement('div');
    bar.className = 'dsy-sticky-atc';
    bar.setAttribute('aria-hidden', 'true');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--primary dsy-sticky-atc__btn';
    button.tabIndex = -1;
    bar.appendChild(button);
    document.body.appendChild(bar);

    const sync = () => {
      const label = source.querySelector('span') || source;
      button.textContent = label.textContent.replace(/\s+/g, ' ').trim();
      button.disabled = source.disabled;
    };
    sync();
    new MutationObserver(sync).observe(source, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });

    button.addEventListener('click', () => source.click());

    const mobile = window.matchMedia('(max-width: 749px)');
    let offscreen = false;
    const update = () => {
      const show = mobile.matches && offscreen;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      button.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle('dsy-sticky-atc-visible', show);
    };
    new IntersectionObserver(([entry]) => {
      offscreen = !entry.isIntersecting;
      update();
    }).observe(source);
    mobile.addEventListener('change', update);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
